// Detaljnivå: samma juridiska innehåll, men färre dokument och färre fält i
// små projekt. Nivån föreslås av SKR-profilen och faktiska förhållanden, och
// kan alltid höjas eller sänkas manuellt med motivering.
//
// Ingenting raderas: dokument som inte ingår i nivån markeras som
// "not_applicable" i process_steps, precis som lättläget gjorde tidigare.
import { supabase } from "@/integrations/supabase/client";
import { PROCESS_STEPS } from "./constants";
import { fetchProjectProfile, type ProjectProfile } from "./simpleMode";
import { applyLevel, computeLevel } from "./projectLevel";


export type DetailLevel = "enkel" | "standard" | "full";
export type DetailLevelSetting = DetailLevel | "auto";

export const DETAIL_LEVEL_LABELS: Record<DetailLevel, string> = {
  enkel: "Enkel",
  standard: "Standard",
  full: "Full",
};

export const DETAIL_LEVEL_DESCRIPTIONS: Record<DetailLevel, string> = {
  enkel: "Litet, kort och lågriskprojekt. Ett samlat minidokument i stället för hela kedjan.",
  standard: "Normalprojekt. Hela grundprocessen utan de mest specialiserade dokumenten.",
  full: "Komplext projekt eller höga risker. Samtliga dokument är aktuella.",
};

/**
 * Dokument som aldrig skalas bort, oavsett nivå. De följer av lag och
 * omfattning – inte av projektets storlek.
 */
export const ALWAYS_REQUIRED_DOCS = [
  "05", // Uppdragsbekräftelse – utsedd Bas-P och Bas-U
  "04", // Förhandsanmälan när gränsen passeras
  "12", // Arbetsmiljöplan
  "13", // Särskilda arbetsmiljörisker
  "28", // Tillbud- och olycksrapportering
  "31", // Kvarvarande bruksrisker
  "22", // Överlämning till byggherre
] as const;

/** Nivå 1: minsta möjliga uppsättning – allt annat samlas i Dok 60. */
export const LEVEL_ENKEL_DOCS = [
  ...ALWAYS_REQUIRED_DOCS,
  "60", // Arbetsmiljöplan liten omfattning (mini-AMP)
  "52", // Delegeringsbrev (enda byggherredokumentet på nivå 1)
  "27", // Skyddsrondsprotokoll
];

/** Nivå 2: grundprocessen för ett normalprojekt. */
export const LEVEL_STANDARD_DOCS = [
  ...LEVEL_ENKEL_DOCS,
  "01",
  "03",
  "06",
  "07",
  "08",
  "09",
  "10",
  "11",
  "14",
  "15",
  "15c",
  "16",
  "20",
  "21",
  "23",
  "24",
  "24b",
  "26",
  "29",
  "30",
  "30b",
  "32",
  "33",
  "34",
  "40",
  "41",
  "42",
  "50",
  "53",
];

const ALL_DOCS = PROCESS_STEPS.map((s) => s.docNumber);

/** Dokument som ingår i respektive nivå. */
export const DOCS_BY_LEVEL: Record<DetailLevel, string[]> = {
  enkel: LEVEL_ENKEL_DOCS.filter((d) => ALL_DOCS.includes(d) || d === "60"),
  standard: LEVEL_STANDARD_DOCS.filter((d) => ALL_DOCS.includes(d) || d === "60"),
  full: ALL_DOCS,
};

/** Dokument som markeras som ej aktuella på en viss nivå. */
export const hiddenDocsForLevel = (level: DetailLevel): string[] => {
  const keep = new Set(DOCS_BY_LEVEL[level]);
  return ALL_DOCS.filter((d) => !keep.has(d));
};

/** Lägsta nivå där dokumentet ingår. */
export const docMinLevel = (docNumber: string): DetailLevel => {
  if (DOCS_BY_LEVEL.enkel.includes(docNumber)) return "enkel";
  if (DOCS_BY_LEVEL.standard.includes(docNumber)) return "standard";
  return "full";
};

export const detailLevelNote = (level: DetailLevel) =>
  `Ej aktuellt på detaljnivå "${DETAIL_LEVEL_LABELS[level]}". Kan slås tillbaka när som helst.`;


/* ------------------------------------------------------------------ */
/* Beräknad nivå                                                        */
/* ------------------------------------------------------------------ */

export interface DetailLevelState {
  /** Nivån som gäller (sparad av beräkningsmotorn). */
  level: DetailLevel;
  /** Nivån motorn räknar fram just nu utifrån lämnade uppgifter. */
  computed: DetailLevel;
  /** Klartext om varför nivån blivit den här. */
  reasons: string[];
  /** True när nivån ligger högre än den beräknade (höjd tidigare, sänks aldrig). */
  raisedAboveComputed: boolean;
  /** Kompletterande signaler från projektets innehåll. */
  profile: ProjectProfile;
}

const RANK: Record<DetailLevel, number> = { enkel: 0, standard: 1, full: 2 };

export const isAtLeast = (level: DetailLevel, min: DetailLevel) => RANK[level] >= RANK[min];

export const levelRank = (level: DetailLevel) => RANK[level];

/**
 * Läser projektets nivå. Nivån ägs av servern – appen kan bara visa den och
 * uppdatera de underliggande uppgifterna.
 */
export const fetchDetailLevel = async (projectId: string): Promise<DetailLevelState> => {
  const [profile, projectRes, computed] = await Promise.all([
    fetchProjectProfile(projectId),
    supabase
      .from("projects")
      .select("detail_level, detail_level_reason")
      .eq("id", projectId)
      .maybeSingle(),
    computeLevel(projectId).catch(() => ({ level: "standard" as DetailLevel, reasons: [] })),
  ]);

  const row = (projectRes.data || null) as {
    detail_level?: string | null;
    detail_level_reason?: string | null;
  } | null;

  const raw = row?.detail_level;
  const stored: DetailLevel | null =
    raw === "enkel" || raw === "standard" || raw === "full" ? raw : null;

  const level = stored ?? computed.level;
  const reasons = [...computed.reasons, ...profile.reasons];

  return {
    level,
    computed: computed.level,
    reasons,
    raisedAboveComputed: RANK[level] > RANK[computed.level],
    profile,
  };
};


/* ------------------------------------------------------------------ */
/* Tillämpning                                                          */
/* ------------------------------------------------------------------ */

const stepRow = (docNumber: string, projectId: string, applicable: boolean, note: string | null) => {
  const def = PROCESS_STEPS.find((s) => s.docNumber === docNumber);
  if (!def) return null;
  return {
    project_id: projectId,
    doc_number: docNumber,
    title: def.title,
    phase: def.phase,
    description: def.description,
    app_link: def.appLink ?? null,
    status: applicable ? "not_started" : "not_applicable",
    not_applicable: !applicable,
    not_applicable_reason: applicable ? null : note,
    sort_order: PROCESS_STEPS.indexOf(def),
  } as Record<string, unknown>;
};

/**
 * Markerar dokument utanför nivån som ej aktuella och gör dokument inom
 * nivån aktuella igen – men rör bara steg som tidigare gömts av nivåstyrningen,
 * så manuella "ej aktuellt"-markeringar med egen motivering behålls.
 */
export const applyDetailLevel = async (projectId: string, level: DetailLevel) => {
  const note = detailLevelNote(level);
  const hidden = new Set(hiddenDocsForLevel(level));

  const { data: existing } = await supabase
    .from("process_steps")
    .select("doc_number, status, not_applicable_reason")
    .eq("project_id", projectId);

  const rows = ALL_DOCS.map((doc) => {
    const current = ((existing || []) as Array<{
      doc_number: string;
      status: string;
      not_applicable_reason: string | null;
    }>).find((r) => r.doc_number === doc);

    if (hidden.has(doc)) {
      // Redan ej aktuellt av annan anledning – lämna orört.
      if (current?.status === "not_applicable" && current.not_applicable_reason && !current.not_applicable_reason.startsWith("Ej aktuellt på detaljnivå")) {
        return null;
      }
      if (current?.status === "in_progress" || current?.status === "done") return null;
      return stepRow(doc, projectId, false, note);
    }

    // Ska ingå: återställ bara det som nivåstyrningen själv gömt.
    if (
      current?.status === "not_applicable" &&
      (current.not_applicable_reason || "").startsWith("Ej aktuellt på detaljnivå")
    ) {
      return stepRow(doc, projectId, true, null);
    }
    return null;
  }).filter(Boolean) as Record<string, unknown>[];

  if (rows.length === 0) return;

  const { error } = await supabase
    .from("process_steps")
    .upsert(rows as never, { onConflict: "project_id,doc_number" });
  if (error) throw new Error(error.message);
};

/* ------------------------------------------------------------------ */
/* Automatisk uppgradering                                              */
/* ------------------------------------------------------------------ */

export interface EscalationResult {
  from: DetailLevel;
  to: DetailLevel;
  reasons: string[];
  changed: boolean;
  newlyApplicable: string[];
}

/**
 * Räknas om när något förändrats i projektet. Servern äger beräkningen och
 * höjer nivån när projektet vuxit – den sänks aldrig automatiskt.
 */
export const recalculateDetailLevel = async (projectId: string): Promise<EscalationResult> => {
  const before = await fetchDetailLevel(projectId);
  const from = before.level;

  const result = await applyLevel(projectId, "app_recalc");
  const to = result.level;

  if (to === from) {
    return { from, to, reasons: result.reasons, changed: false, newlyApplicable: [] };
  }

  const beforeDocs = new Set(DOCS_BY_LEVEL[from]);
  const newlyApplicable = DOCS_BY_LEVEL[to].filter((d) => !beforeDocs.has(d));
  await applyDetailLevel(projectId, to);

  return { from, to, reasons: result.reasons, changed: true, newlyApplicable };
};

